import type { ReactNode } from "react";
import { usePortalAuth } from "../PortalAuthContext";
import { type PortalTabId } from "./PortalTabMatrix";
import { PortalSidebar } from "./PortalSidebar";
import { PortalTopBar } from "./PortalTopBar";
import { PortalNotificationsProvider } from "./PortalNotificationsContext";
import { NewConnectionBadgeProvider } from "./NewConnectionBadgeContext";
import { PortalRequestsBadgeProvider } from "./PortalRequestsBadgeContext";
import { CertificationWelcomeModal } from "./CertificationWelcomeModal";
import { ApplicationReceivedModal } from "./ApplicationReceivedModal";
import { PreApprovalCelebrationModal } from "./PreApprovalCelebrationModal";
import { AccountDisabledPage } from "./AccountDisabledPage";
import { PortalForcedPasswordChange } from "./PortalForcedPasswordChange";
import { PortalImpersonationBanner } from "./PortalImpersonationBanner";

// RECON→FIX (UAT 2026-05-30) — the persistent portal chrome (left sidebar
// + full top bar + centered content), extracted from PortalDashboardShell
// so non-?tab= routes (the deal-submission flow) can render inside the
// same shell instead of the bare PortalShell. The dashboard drives the
// active tab from ?tab=; the deal flow pins it to "deals" and navigates
// away on select. Both share this single chrome so the lineup, status
// badge, and welcome modal stay identical everywhere.
interface PortalChromeProps {
  /** Right-aligned title shown in the top bar (e.g. "Submit a deal"). */
  pageTitle: string;
  /** Which sidebar entry renders as active. */
  activeTab: PortalTabId;
  /** Called when a sidebar entry is chosen. */
  onSelectTab: (next: PortalTabId) => void;
  children: ReactNode;
}

export function PortalChrome({
  pageTitle,
  activeTab,
  onSelectTab,
  children,
}: PortalChromeProps) {
  const auth = usePortalAuth();

  if (!auth.me) {
    return (
      <div className="min-h-screen bg-white-warm flex items-center justify-center text-small text-gray-mid">
        Loading…
      </div>
    );
  }

  // Disable / Re-enable / Archive — a disabled account (cookie may still be
  // valid) gets the full-page replacement instead of any portal navigation.
  //
  // The impersonation banner renders ABOVE it rather than the short-circuit
  // being skipped: seeing exactly what a disabled account sees is a legitimate
  // reason for an admin to be here, so the page must stay reachable — but the
  // admin still needs a way out, and this page offers no navigation of its own.
  if (auth.me.isDisabled) {
    return (
      <>
        <PortalImpersonationBanner />
        <AccountDisabledPage
          email={auth.me.supportContactEmail}
          phone={auth.me.supportContactPhone}
        />
      </>
    );
  }

  // Admin/staff-initiated password reset — a staff-assigned temporary password
  // blocks every authed route behind a forced change-password screen until the
  // user picks their own. Sits below the disabled gate (a disabled account
  // never reaches this) and above all portal navigation.
  //
  // …EXCEPT while impersonating, which SKIPS this gate entirely (unlike the
  // disabled page above, which is preserved). Every freshly-invited portal
  // account carries this flag, so without the bypass an admin impersonating one
  // would be trapped on a screen whose only exit is setting that account's real
  // password — the one thing impersonation must never do. The flag stays
  // honest; only the gate yields. Do not "tidy" this back to a bare check.
  if (auth.me.mustChangePassword && !auth.me.impersonation) {
    return <PortalForcedPasswordChange />;
  }

  return (
    <PortalNotificationsProvider>
      <NewConnectionBadgeProvider>
      <PortalRequestsBadgeProvider>
      <div className="min-h-screen bg-white-warm text-onyx flex">
        <PortalSidebar activeTab={activeTab} onSelect={onSelectTab} />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Above the top bar so it is the first thing on every portal
              route while an admin is acting as this account. */}
          <PortalImpersonationBanner />
          <PortalTopBar pageTitle={pageTitle} />
          <main className="flex-1 px-space-4 md:px-space-6 py-space-5 max-w-7xl w-full mx-auto">
            {/* Round-3 #10 — broker certification status used to render here,
                above every tab; fix-broker-portal moved it into the top bar
                (PortalTopBar) so it no longer bleeds into the content area. */}
            {children}
          </main>
        </div>
        {/* UAT FixBatch FIND-17 — one-time certification welcome on portal load. */}
        {auth.me.accountType === "Broker" && <CertificationWelcomeModal />}
        {/* Broker self-apply follow-ups — one-time "Application received"
            welcome for self-applied brokers on first login. */}
        {auth.me.accountType === "Broker" && <ApplicationReceivedModal />}
        {/* Sender-Fields v1 — one-time "you've been pre-approved" celebration
            for buyer/seller recipients with a newly-issued letter. */}
        {(auth.me.accountType === "Buyer" || auth.me.accountType === "Seller") && (
          <PreApprovalCelebrationModal />
        )}
      </div>
      </PortalRequestsBadgeProvider>
      </NewConnectionBadgeProvider>
    </PortalNotificationsProvider>
  );
}
